import React, { useEffect, useState, useRef } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'

export default function Lightbox({ images, currentIndex, onClose, onNavigate }) {
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)
  const lightboxRef = useRef(null)

  // Minimum swipe distance in pixels
  const minSwipeDistance = 50

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') onNavigate((currentIndex + 1) % images.length)
      if (e.key === 'ArrowLeft') onNavigate((currentIndex - 1 + images.length) % images.length)
    }

    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    // Focus the lightbox container for accessibility
    if (lightboxRef.current) {
      lightboxRef.current.focus()
    }

    return () => {
      document.body.style.overflow = 'unset'
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [currentIndex, images.length, onClose, onNavigate])

  const onTouchStart = (e) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      // Next image
      onNavigate((currentIndex + 1) % images.length)
    } else if (isRightSwipe) {
      // Previous image
      onNavigate((currentIndex - 1 + images.length) % images.length)
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      ref={lightboxRef}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md transition-opacity duration-300 outline-none"
      onClick={handleBackdropClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 rounded-full bg-white/10 p-3 text-white transition-all hover:bg-white/20 active:scale-95 cursor-pointer"
        aria-label="Luk galleri"
      >
        <X size={24} />
      </button>

      {/* Left Navigation */}
      {images.length > 1 && (
        <button
          onClick={() => onNavigate((currentIndex - 1 + images.length) % images.length)}
          className="absolute left-4 z-50 rounded-full bg-white/10 p-3 text-white transition-all hover:bg-white/20 active:scale-95 max-sm:hidden cursor-pointer"
          aria-label="Forrige billede"
        >
          <ChevronLeft size={28} />
        </button>
      )}

      {/* Image Container */}
      <div className="relative max-h-[85vh] max-w-[90vw] flex flex-col items-center justify-center">
        <img
          src={images[currentIndex]}
          alt={`Galleri billede ${currentIndex + 1}`}
          className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl select-none animate-fade-in"
          draggable={false}
        />
        
        {/* Counter */}
        <div className="mt-4 text-sm font-medium text-stone-300">
          {currentIndex + 1} af {images.length}
        </div>
      </div>

      {/* Right Navigation */}
      {images.length > 1 && (
        <button
          onClick={() => onNavigate((currentIndex + 1) % images.length)}
          className="absolute right-4 z-50 rounded-full bg-white/10 p-3 text-white transition-all hover:bg-white/20 active:scale-95 max-sm:hidden cursor-pointer"
          aria-label="Næste billede"
        >
          <ChevronRight size={28} />
        </button>
      )}

      {/* Mobile Swipe Indicator (Only shown briefly or subtle) */}
      <div className="absolute bottom-4 text-xs text-stone-500 sm:hidden">
        Swipe til højre/venstre for at skifte billede
      </div>
    </div>
  )
}
